<template>
  <div class="game-records-page">
    <title-bar :title="$lang.billGame_title || 'GAME RECORDS'" />

    <!-- Balance + Filter Header -->
    <div class="gr-subheader">
      <div class="gr-balance">
        <span class="gr-balance__label">{{ getCurrency }}</span>
        <span class="gr-balance__amount">{{ $formatNumberWithCommas(balance) }}</span>
      </div>
      <div class="gr-filter">
        <van-dropdown-menu>
          <van-dropdown-item v-model="filterValue" :options="filterOptions" @change="onFilterChange" />
        </van-dropdown-menu>
      </div>
    </div>
    <div v-if="balanceReadError" class="gr-diagnostic">
      {{ balanceReadError }}
    </div>
    <div v-if="recordReadError" class="gr-diagnostic">
      {{ recordReadError }}
    </div>

    <!-- Game List -->
    <van-list v-model="loading" :finished="finished" loading-text=" " @load="loadMore" class="gr-list">
      <template v-for="(group, gIdx) in groupedList">
        <div class="gr-date" :key="'d'+gIdx">{{ group.label }}</div>
        <div
          v-for="(item, idx) in group.items"
          :key="'g'+gIdx+'-'+idx"
          class="gr-card"
        >
          <div class="gr-card__left">
            <div class="gr-card__thumb">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#ff5a1f"><path d="M21.58 16.09l-1.09-7.66C20.21 6.46 18.52 5 16.53 5H7.47C5.48 5 3.79 6.46 3.51 8.43l-1.09 7.66C2.2 17.63 3.39 19 4.94 19h0c.68 0 1.32-.27 1.8-.75L9 16h6l2.25 2.25c.48.48 1.13.75 1.8.75h0c1.55 0 2.74-1.37 2.53-2.91zM11 11H9v2H8v-2H6v-1h2V8h1v2h2v1zm4 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2-3c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>
            </div>
            <div class="gr-card__info">
              <p class="gr-card__name">{{ item.gameName }}</p>
              <p class="gr-card__meta">
                <span class="gr-card__bet">{{ $lang.billGame_txt2 || 'Bet:' }} {{ getCurrency }}{{ $formatNumberWithCommas(item.betAmount) }}</span>
              </p>
              <p class="gr-card__time">{{ formatTime(item.createDate) }}</p>
            </div>
          </div>
          <div class="gr-card__right">
            <p class="gr-card__profit" :class="getProfit(item) >= 0 ? 'gr-card__profit--win' : 'gr-card__profit--loss'">
              {{ getCurrency }} {{ profitSign(item) }}{{ $formatNumberWithCommas(Math.abs(getProfit(item))) }}
            </p>
          </div>
        </div>
      </template>

      <van-empty
        v-if="!userList.length && !loading"
        :image="require('../../../assets/img/common/img_no_data.png')"
        :description="$lang.noempt"
      />
    </van-list>
  </div>
</template>

<script>
import { GetUserGameRecordList, GameBalanceList } from '@/api/common'
import { contentOf, failMessage, firstArray, handleNeedLogin, isOk, toNumber } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'

function safeToast(vm, payload) {
  if (vm && typeof vm.$toast === 'function') vm.$toast(payload)
}

function billFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

function fallbackToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasReadFallbackMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasReadFallbackMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const keyToken = fallbackToken(key)
    const valueToken = fallbackToken(value)
    if (keyToken.includes('fallback') && (value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(valueToken) || valueToken.includes('fallback'))) return true
    if (value && typeof value === 'object') return hasReadFallbackMarker(value, depth + 1, seen)
    return valueToken.includes('fallback')
  })
}

function isActiveReadMarker(value) {
  const token = fallbackToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(token)
}

function hasServerReadProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasServerReadProof(item, depth + 1, seen))
  if (['serverReadProof', 'server_read_proof', 'readProof', 'read_proof'].some(key => isActiveReadMarker(payload[key]))) return true
  return Object.keys(payload).some(key => {
    const value = payload[key]
    return value && typeof value === 'object' && hasServerReadProof(value, depth + 1, seen)
  })
}

function hasOkReadEnvelopeMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasOkReadEnvelopeMarker(item, depth + 1, seen))
  const okValues = [
    payload.status,
    payload.state,
    payload.result
  ].some(value => ['ok', 'success', 'successful', 'succeed', 'succeeded'].includes(fallbackToken(value))) ||
    [
      payload.code,
      payload.errorCode
    ].some(value => ['0', '00', '0000', '000000', '200'].includes(fallbackToken(value))) ||
    isActiveReadMarker(payload.success)
  if (okValues) return true
  return ['content', 'data', 'payload', 'page', 'body'].some(key => {
    const value = payload[key]
    return value && typeof value === 'object' && hasOkReadEnvelopeMarker(value, depth + 1, seen)
  })
}

function hasReadUnavailableMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasReadUnavailableMarker(item, depth + 1, seen))
  const markerKeys = [
    'readOnly',
    'readOnlyAction',
    'readOnlyMode',
    'readonly',
    'read_only',
    'read_only_action',
    'read_only_mode',
    'mock',
    'mockMode',
    'mock_mode',
    'mockFallback',
    'mock_fallback',
    'unimplemented',
    'notImplemented',
    'not_implemented',
    'dataUnavailable',
    'data_unavailable',
    'fallback',
    'fallbackUsed',
    'fallback_used',
    'unsupported',
    'notSupported',
    'not_supported',
    'notConnected',
    'not_connected',
    'readFailure',
    'read_failure',
    'readFailed',
    'read_failed',
    'failed',
    'failure',
    'queryOnly',
    'query_only',
    'onlyQuery',
    'only_query',
    'blocked',
    'writeBlocked',
    'write_blocked',
    'backendBlocked',
    'backend_blocked',
    'backendFailure',
    'backend_failure',
    'technicalMarker',
    'technical_marker'
  ]
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const keyToken = fallbackToken(key)
    const valueToken = fallbackToken(value)
    const isBlockedText = (valueToken.includes('blocked') && !valueToken.includes('unblocked') && !valueToken.includes('not blocked')) ||
      valueToken.includes('backend_blocked') ||
      valueToken.includes('backendblocked') ||
      valueToken.includes('backend blocked') ||
      valueToken.includes('backend-blocked') ||
      valueToken.includes('backend_failure') ||
      valueToken.includes('backendfailure') ||
      valueToken.includes('backend failure') ||
      valueToken.includes('backend-failure') ||
      valueToken.includes('technical_marker') ||
      valueToken.includes('technicalmarker') ||
      valueToken.includes('technical marker') ||
      valueToken.includes('technical-marker')
    const activeValue = isActiveReadMarker(value)
    const textMarkerKey = [
      'status',
      'state',
      'result',
      'code',
      'errorcode',
      'msg',
      'message',
      'errormsg',
      'errormessage',
      'reason',
      'desc',
      'description',
      'readonlymessage'
    ].includes(keyToken)
    if (['readOnlyQuery', 'read_only_query'].includes(key) && activeValue && !hasServerReadProof(payload)) return true
    if (markerKeys.includes(key) && activeValue) return true
    if (keyToken.includes('unavailable') && activeValue) return true
    if (value && typeof value === 'object') return hasReadUnavailableMarker(value, depth + 1, seen)
    return textMarkerKey && (valueToken.includes('dataunavailable') ||
      valueToken.includes('data_unavailable') ||
      valueToken.includes('data unavailable') ||
      valueToken.includes('fallback') ||
      valueToken.includes('mock') ||
      valueToken.includes('read_only') ||
      valueToken.includes('read only') ||
      valueToken.includes('readonly') ||
      valueToken.includes('unimplemented') ||
      valueToken.includes('not_implemented') ||
      valueToken.includes('not implemented') ||
      valueToken.includes('unsupported') ||
      valueToken.includes('not_supported') ||
      valueToken.includes('not supported') ||
      valueToken.includes('not_connected') ||
      valueToken.includes('not connected') ||
      valueToken.includes('read_failure') ||
      valueToken.includes('read failure') ||
      valueToken.includes('read_failed') ||
      valueToken.includes('read failed') ||
      valueToken.includes('failed') ||
      valueToken.includes('query_only') ||
      valueToken.includes('query only') ||
      isBlockedText ||
      valueToken.includes('只支持查询') ||
      valueToken.includes('仅支持查询'))
  })
}

function hasTerminalReadUnavailableMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasTerminalReadUnavailableMarker(item, depth + 1, seen))
  const terminalKeys = [
    'backendBlocked',
    'backend_blocked',
    'backendFailure',
    'backend_failure',
    'technicalMarker',
    'technical_marker',
    'dataUnavailable',
    'data_unavailable',
    'readOnly',
    'readOnlyMode',
    'read_only_mode',
    'readonly',
    'read_only',
    'writeBlocked',
    'write_blocked'
  ]
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const keyToken = fallbackToken(key)
    const valueToken = fallbackToken(value)
    const activeValue = value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(valueToken)
    if (terminalKeys.includes(key) && activeValue) return true
    if (value && typeof value === 'object') return hasTerminalReadUnavailableMarker(value, depth + 1, seen)
    return ['status', 'state', 'result', 'code', 'msg', 'message', 'errormsg', 'errormessage', 'reason', 'desc', 'description'].includes(keyToken) &&
      (valueToken.includes('backend blocked') ||
        valueToken.includes('backend-blocked') ||
        valueToken.includes('backend_blocked') ||
        valueToken.includes('backendblocked') ||
        valueToken.includes('backend failure') ||
        valueToken.includes('backend-failure') ||
        valueToken.includes('backend_failure') ||
        valueToken.includes('backendfailure') ||
        valueToken.includes('technical marker') ||
        valueToken.includes('technical-marker') ||
        valueToken.includes('technical_marker') ||
        valueToken.includes('technicalmarker') ||
        valueToken.includes('data unavailable') ||
        valueToken.includes('data_unavailable') ||
        valueToken.includes('dataunavailable'))
  })
}

function isReadOk(res) {
  return (isOk(res) || (hasServerReadProof(res) && hasOkReadEnvelopeMarker(res))) &&
    !hasReadFallbackMarker(res) &&
    !hasReadUnavailableMarker(res)
}

const READ_ENVELOPE_KEYS = ['content', 'data', 'payload', 'page', 'body']
const BALANCE_KEYS = ['balance', 'totalBalance', '_balUsable', 'balUsable', 'availableBalance']

function normalizeReadEnvelope(payload, depth = 0, seen = []) {
  const content = contentOf(payload)
  const source = content && typeof content === 'object' ? content : {}
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return source
  seen.push(payload)
  return READ_ENVELOPE_KEYS.reduce((merged, key) => {
    const value = payload[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) return merged
    const nested = normalizeReadEnvelope(value, depth + 1, seen)
    return Object.keys(nested).length ? { ...merged, ...nested } : merged
  }, source)
}

function firstDefinedValue(source, keys) {
  const payload = source && typeof source === 'object' ? source : {}
  const key = keys.find(name => payload[name] !== undefined && payload[name] !== null && payload[name] !== '')
  return key ? payload[key] : 0
}

function readBalanceAmount(payload) {
  return toNumber(firstDefinedValue(normalizeReadEnvelope(payload), BALANCE_KEYS))
}

function readGameRecordList(payload) {
  const content = normalizeReadEnvelope(payload)
  return firstArray(content, ['dataList', 'gameRecordList', 'list', 'records', 'items', 'pageList'])
}

function readDateRange(range) {
  if (Array.isArray(range)) return [range[0] || '', range[1] || '']
  const value = String(range == null ? '' : range)
  if (value.includes('&')) {
    const [startDate = '', endDate = ''] = value.split('&')
    return [startDate, endDate]
  }
  return [value.slice(0, 19), value.slice(20, 40)]
}

export default {
  name: 'BillGame',
  data() {
    return {
      balance: 0,
      userList: [],
      loading: false,
      finished: false,
      page: 0,
      filterValue: 6,
      filterOptions: [
        { text: this.$lang.billGame_txt4 || 'Today', value: 0 },
        { text: this.$lang.billGame_txt5 || 'Yesterday', value: 1 },
        { text: this.$lang.billGame_txt6 || 'This Week', value: 2 },
        { text: this.$lang.billGame_txt7 || 'Last Week', value: 3 },
        { text: this.$lang.billGame_txt8 || 'This Month', value: 4 },
        { text: this.$lang.billGame_txt9 || 'Last Month', value: 5 },
        { text: this.$lang.billGame_txt10 || 'All', value: 6 }
      ],
      params: {},
      pageSize: 20,
      balanceReadError: '',
      recordReadError: '',
      replaceListOnSuccess: false,
      pendingRecordReadSnapshot: null
    }
  },
  computed: {
    groupedList() {
      const groups = {}
      const today = this.$dayjs().format('YYYY-MM-DD')
      this.userList.forEach(item => {
        const parsed = item.createDate ? this.$dayjs(item.createDate) : null
        const date = parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD') : '--'
        if (!groups[date]) {
          groups[date] = {
            date,
            label: date === today ? (this.$lang.billGame_txt4 || 'Today') : date,
            items: []
          }
        }
        groups[date].items.push(item)
      })
      return Object.values(groups)
    }
  },
  mounted() {
    if (!this.ensureLogin()) {
      this.finished = true
      return
    }
    this.getBalance()
  },
  methods: {
    ensureLogin() {
      const storage = getLocalStorage()
      if (safeGetStorageItem(storage, 'token')) return true
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$jumpTo('/home', {}, { replace: true })
      return false
    },
    async getBalance() {
      if (!this.ensureLogin()) return
      try {
        const data = await GameBalanceList()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        if (isReadOk(data)) {
          this.balanceReadError = ''
          this.balance = readBalanceAmount(data)
        } else {
          this.balanceReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.balanceReadError, icon: 'cross' })
        }
      } catch (e) {
        this.balanceReadError = billFailureMessage(e, '当前余额读取失败，已保留已加载余额。')
        safeToast(this, { message: this.balanceReadError, icon: 'cross' })
      }
    },
    formatTime(d) {
      const parsed = d ? this.$dayjs(d) : null
      return parsed && parsed.isValid() ? parsed.format('HH:mm') : '--'
    },
    getProfit(item) {
      return toNumber(item.winAmount) - toNumber(item.betAmount)
    },
    profitSign(item) {
      return this.getProfit(item) < 0 ? '-' : '+'
    },
    onFilterChange(i) {
      if (!this.ensureLogin()) return
      this.resetListForFilter()
      const rangeMap = {
        0: () => this.$getTodayRange(),
        1: () => this.$getYesterdayRange(),
        2: () => this.$getThisWeekRange(),
        3: () => this.$getLastWeekRange(),
        4: () => this.$getThisMonthRange(),
        5: () => this.$getLastMonthRange()
      }
      if (rangeMap[i]) {
        const range = rangeMap[i]()
        const [startDate, endDate] = readDateRange(range)
        this.fetchData(startDate, endDate)
      } else {
        this.fetchData('', '')
      }
    },
    resetList() {
      this.userList = []
      this.page = 0
      this.finished = false
      this.replaceListOnSuccess = false
      this.pendingRecordReadSnapshot = null
    },
    resetListForFilter() {
      this.pendingRecordReadSnapshot = this.recordReadSnapshot()
      this.page = 0
      this.finished = false
      this.replaceListOnSuccess = true
    },
    recordReadSnapshot() {
      return {
        userList: Array.isArray(this.userList) ? this.userList.filter(item => item && item.__h5TrustedRecord === true) : [],
        page: this.page,
        finished: this.finished,
        params: { ...(this.params || {}) }
      }
    },
    restoreRecordReadSnapshot(snapshot) {
      if (!snapshot) return
      this.userList = snapshot.userList
      this.page = snapshot.page
      this.finished = snapshot.finished
      this.params = snapshot.params
      this.replaceListOnSuccess = false
      this.pendingRecordReadSnapshot = null
    },
    gameRecordKey(item) {
      const record = item || {}
      const parts = [
        record.orderNo || record.orderId || record.id || record.gameRecordId,
        record.gameName || record.name,
        record.createDate || record.createdAt || record.time,
        record.betAmount || record.bet,
        record.winAmount || record.win
      ].map(value => value === undefined || value === null ? '' : String(value))
      return parts.some(Boolean) ? parts.join('|') : ''
    },
    mergeGameRecordList(list) {
      const seen = new Set(this.userList.map(item => this.gameRecordKey(item)).filter(Boolean))
      const rows = Array.isArray(list) ? list : []
      const next = rows.filter(item => {
        const key = this.gameRecordKey(item)
        if (!key) return true
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      this.userList = this.userList.concat(next)
    },
    replaceGameRecordList(list) {
      this.userList = []
      this.mergeGameRecordList(list)
    },
    async fetchData(startDate, endDate) {
      if (!this.ensureLogin()) {
        this.finished = true
        return
      }
      if (this.finished && this.page > 0) return
      const previousReadState = this.pendingRecordReadSnapshot || this.recordReadSnapshot()
      this.params = { startDate, endDate, pageIndex: this.page, pageSize: this.pageSize }
      this.loading = true
      try {
        const data = await GetUserGameRecordList(this.params)
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.finished = true
          return
        }
        if (hasReadUnavailableMarker(data)) {
          this.recordReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          if (typeof this.$toast === 'function') this.$toast({ message: this.recordReadError, icon: 'cross' })
          this.restoreRecordReadSnapshot(previousReadState)
          if (hasTerminalReadUnavailableMarker(data)) {
            this.finished = true
          }
          return
        }
        if (isReadOk(data)) {
          this.recordReadError = ''
          const list = readGameRecordList(data).map(item => ({
            ...item,
            __h5TrustedRecord: true,
            gameName: item.gameName || item.name || '--',
            betAmount: toNumber(item.betAmount || item.bet),
            winAmount: toNumber(item.winAmount || item.win),
            createDate: item.createDate || item.createdAt || item.time || ''
          }))
          if (this.replaceListOnSuccess && this.page === 0) {
            this.replaceGameRecordList(list)
          } else {
            this.mergeGameRecordList(list)
          }
          this.replaceListOnSuccess = false
          this.pendingRecordReadSnapshot = null
          if (list.length < this.pageSize) this.finished = true
          this.page++
        } else {
          this.recordReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.recordReadError, icon: 'cross' })
          this.restoreRecordReadSnapshot(previousReadState)
        }
      } catch (e) {
        this.recordReadError = billFailureMessage(e, '当前游戏记录读取失败，已保留已加载记录。')
        safeToast(this, { message: this.recordReadError, icon: 'cross' })
        this.restoreRecordReadSnapshot(previousReadState)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      const { startDate, endDate } = this.params
      this.fetchData(startDate || '', endDate || '')
    }
  }
}
</script>

<style lang="less" scoped>
@bg: @vk-bg;       /* 页面底 */
@card: @vk-surface;/* 卡片面 */
@neon: @brand;     /* 绿→火焰橙(强调/激活) */
@muted: @vk-t2;    /* 次文字 */
@border: rgba(255, 255, 255, 0.06);

.game-records-page {
  min-height: 100vh;
  background: @bg;
  padding-bottom: 20px;
}

.gr-diagnostic {
  margin: 8px 16px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #fef3c7;
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

// ====== SUB HEADER ======
.gr-subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  position: sticky;
  top: 46px;
  z-index: 10;
  background: rgba(26, 29, 41, 0.95);
  backdrop-filter: blur(8px);
}

.gr-balance {
  background: @card;
  border-radius: 8px;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid @border;

  &__label {
    font-size: var(--vk-fs-xs);
    color: @muted;
  }

  &__amount {
    font-size: var(--vk-fs-base);
    font-weight: 700;
    color: #ffffff;
  }
}

.gr-filter {
  ::v-deep .van-dropdown-menu__bar {
    background: @card;
    height: 32px !important;
    border-radius: 8px;
    border: 1px solid @border;
    padding: 0 12px;
    box-shadow: none;

    .van-dropdown-menu__title {
      color: #ffffff !important;
      font-size: var(--vk-fs-xs) !important;
    }
  }

  ::v-deep .van-dropdown-item {
    border-radius: 10px;
    overflow: hidden;

    .van-cell {
      background: @card;
      color: #ffffff;
      padding: 8px 16px;
      font-size: var(--vk-fs-xs);
    }

    .van-dropdown-item__option--active {
      color: @neon !important;
    }

    .van-dropdown-item__option--active .van-dropdown-item__icon {
      color: @neon !important;
    }
  }

  ::v-deep .van-overlay {
    background: transparent !important;
  }
}

// ====== DATE HEADER ======
.gr-date {
  padding: 12px 16px 6px;
  font-size: var(--vk-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: @muted;
  position: sticky;
  top: 98px;
  background: rgba(26, 29, 41, 0.9);
  backdrop-filter: blur(8px);
  z-index: 5;
}

// ====== GAME CARD ======
.gr-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin: 0 12px 8px;
  background: @card;
  border-radius: 10px;
  border: 1px solid @border;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__thumb {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255, 90, 31, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: var(--vk-fs-base);
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
  }

  &__meta {
    font-size: var(--vk-fs-xs);
    color: @muted;
  }

  &__bet {
    color: @muted;
  }

  &__time {
    font-size: var(--vk-fs-xs);
    color: #4b5563;
  }

  &__right {
    text-align: right;
  }

  &__profit {
    font-size: var(--vk-fs-15);
    font-weight: 700;

    &--win {
      color: @neon;
    }

    &--loss {
      color: var(--vk-danger);
    }
  }
}

// ====== EMPTY STATE ======
::v-deep .van-empty {
  padding: 60px 0;

  .van-empty__description {
    color: @muted;
  }
}

::v-deep .van-loading {
  background: transparent;
}

@media (min-width: 769px) {
  .game-records-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }

  .gr-filter {
    ::v-deep .van-dropdown-item {
      left: auto !important;
      right: 16px !important;
      transform: none !important;
    }
  }
}
</style>
